import type { MaybeRef, MaybeRefOrGetter, Ref } from 'vue'
import type { RouteParamValueRaw } from 'vue-router'

type RouteQueryValueRaw = RouteParamValueRaw | string[]

interface ReactiveRouteOptions {
  mode?: MaybeRef<'replace' | 'push'>
  route?: ReturnType<typeof useRoute>
  router?: ReturnType<typeof useRouter>
}

interface ReactiveRouteOptionsWithTransform<V, R> extends ReactiveRouteOptions {
  transform?: ((val: V) => R) | {
    get?: (value: V) => R
    set?: (value: R) => V
  }
}

const _queue = new WeakMap<ReturnType<typeof useRouter>, Map<string, RouteQueryValueRaw | undefined>>()

function isSameValue(left: RouteQueryValueRaw | undefined, right: RouteQueryValueRaw | undefined) {
  return left === right
}

/**
 * Like a route query ref, but only stays in sync while the current route's path
 * matches the path it was created on. Prevents stale reads/writes when a component
 * persists across a route change (e.g. via keep-alive or a shared layout).
 */
export function useScopedRouteQuery(name: string): Ref<undefined | null | string | string[]>
export function useScopedRouteQuery<T extends RouteQueryValueRaw = RouteQueryValueRaw, K = T>(name: string, defaultValue?: MaybeRefOrGetter<T>, options?: ReactiveRouteOptionsWithTransform<T, K>): Ref<K>
export function useScopedRouteQuery<T extends RouteQueryValueRaw = RouteQueryValueRaw, K = T>(name: string, defaultValue?: MaybeRefOrGetter<T>, options: ReactiveRouteOptionsWithTransform<T, K> = {}) {
  const { mode = 'replace', route = useRoute(), router = useRouter(), transform } = options
  const scopedPath = route.path

  let transformGet = (value: T) => value as unknown as K
  let transformSet = (value: K) => value as unknown as T
  let trigger = () => {}

  if (typeof transform === 'function') {
    transformGet = transform
  }
  else if (transform) {
    if (transform.get)
      transformGet = transform.get
    if (transform.set)
      transformSet = transform.set
  }

  if (!_queue.has(router))
    _queue.set(router, new Map())

  const queriesQueue = _queue.get(router)!
  let query = route.query[name] as RouteQueryValueRaw | undefined

  watch(() => route.path, (currentPath) => {
    if (currentPath !== scopedPath)
      return

    const currentQuery = route.query[name] as RouteQueryValueRaw | undefined
    if (isSameValue(query, currentQuery))
      return

    query = currentQuery
    trigger()
  }, { flush: 'sync' })

  watch(() => route.query[name], (currentQuery) => {
    if (route.path !== scopedPath)
      return

    if (isSameValue(query, currentQuery as RouteQueryValueRaw | undefined))
      return

    query = currentQuery as RouteQueryValueRaw | undefined
    trigger()
  }, { flush: 'sync' })

  const proxy = customRef((track, _trigger) => {
    trigger = _trigger

    return {
      get() {
        track()
        return transformGet(query !== undefined ? query as T : toValue(defaultValue) as T)
      },
      set(value: K) {
        const transformedValue = transformSet(value)

        if (query === transformedValue)
          return

        query = transformedValue === toValue(defaultValue) ? undefined : transformedValue as unknown as RouteQueryValueRaw

        if (route.path === scopedPath)
          queriesQueue.set(name, query)

        _trigger()

        nextTick(() => {
          if (route.path !== scopedPath || queriesQueue.size === 0)
            return

          const newQueries = Object.fromEntries(queriesQueue.entries())
          queriesQueue.clear()
          const { params, query: currentQuery, hash } = route
          router[toValue(mode)]({
            params,
            query: {
              ...currentQuery,
              ...newQueries,
            },
            hash,
          })
        })
      },
    }
  })

  return proxy
}
